import { Button } from "@/components/ui/button";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { GamePageSection } from "@/context/AnalyticsProvider/gamePage";
import { AnimatedEffect } from "@components/NewGamePage/GPOnboarding/AnimatedEffect";
import Image from "next/image";
import { GlassButton } from "../GlassButton";
import { TermsAndPrivacy } from "./TermsAndPrivacy";
import type { GPOnboardingStepProps } from "./utils";

export const OnboardingInGame = ({
  onNext,
  isVisible,
  track,
}: GPOnboardingStepProps) => {
  const handleDontHaveSteamClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "get-started-no-steam",
        controlLabel: "Don't have Steam?",
        controlType: ControlType.LINK,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    onNext();
  };

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Background Layer */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[#131313]" />
        <div className="absolute inset-0 overflow-hidden">
          <Image
            src="/images/games/8ball/8ball-bg2.webp"
            alt=""
            fill
            className="object-cover"
            style={{ objectPosition: "60% center" }}
          />
        </div>
        <div className="absolute inset-0 bg-black/80" />
      </div>

      {/* Imagery Layer */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Merged Header Image - Top background, logo, prize cube, and animation */}
        <div className="absolute inset-x-0 top-2 sm:top-6 h-full w-full">
          <div className="relative mx-auto w-fit">
            <Image
              src="/images/games/8ball/onboarding-header-ingame.webp"
              alt=""
              width={416}
              height={465}
              priority
            />
            {/* Wishlist Badge Overlay - Top Left of Image */}
            <div className="absolute left-0 top-0 -rotate-6 pointer-events-none">
              <Image
                src="/images/games/8ball/wishlist-now-transparent.webp"
                alt=""
                width={195}
                height={63}
                className="object-contain"
              />
            </div>
            {/* Logo Overlay */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none">
              <Image
                src="/images/games/8ball/logo.webp"
                alt="8 Ball by Pokerist"
                width={232}
                height={100}
                className="object-contain"
              />
            </div>
          </div>
        </div>
      </div>

      {/* UI Content Layer */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-between bg-gradient-to-b from-transparent from-[75%] to-black/20 px-5
        pt-[clamp(17rem,33vh,20rem)] sm:pt-[clamp(21.25rem,36vh,21.875rem)]
        pb-[clamp(2rem,8vh,7.5rem)]"
      >
        {/* Title */}
        <div className="flex w-full shrink-0 flex-col items-center gap-[clamp(1rem,4vh,1.5rem)]">
          <p className="text-center font-suisse text-[clamp(1.5rem,6vw,2rem)] font-bold leading-[clamp(2rem,6.5vw,2.5rem)] text-[#f7f7f7]">
            Flex on the pros with a
            <br />
            bonus deluxe crate!
          </p>
          <p className="whitespace-pre-wrap text-center font-roboto text-xs font-bold uppercase leading-5 tracking-[0.125rem] text-[#f7f7f7]">
            Only 5 remaining
          </p>
        </div>

        {/* Buttons */}
        <div className="relative flex w-full shrink-0 flex-col items-center gap-[clamp(1.5rem,4vh,2rem)]">
          <div className="relative mt-[3.125rem] sm:mt-[4.5rem] w-full max-w-[23.25rem]">
            <GlassButton
              onClick={() => onNext()}
              control="get-started"
              section={GamePageSection.ONBOARDING_MODAL}
              className="!h-[clamp(3.75rem,9vh,4.5rem)] w-full min-h-[3.75rem] max-h-[4.5rem] flex-col px-8 py-2.5"
              variant="cta"
            >
              <p className="text-base font-medium leading-6 text-foreground">
                Get your crate on Steam now
              </p>
            </GlassButton>

            {/* Prize Cube Overlay on Button - Responsive positioning */}
            <div className="absolute left-1/2 top-0 size-[clamp(3.75rem,18vw,4.6875rem)] -translate-x-1/2 -translate-y-3/4 pointer-events-none">
              <div className="absolute left-1/2 top-1/2 z-0 h-[clamp(6rem,15vh,8rem)] w-[clamp(8rem,20vw,10rem)] -translate-x-1/2 -translate-y-1/2">
                <AnimatedEffect
                  className="h-full w-full animate-pulse"
                  tint="#fcca74"
                />
              </div>
              <div className="absolute inset-0 overflow-hidden">
                <Image
                  src="/images/games/8ball/onboarding-prize-cube2.webp"
                  alt=""
                  fill
                  className="object-contain"
                />
              </div>
            </div>
          </div>

          {/* Don't have Steam Button */}
          <Button
            onClick={handleDontHaveSteamClick}
            variant="ghost"
            className="h-9 rounded-[1.375rem] bg-transparent px-4 py-2 text-sm text-foreground"
          >
            Don't have Steam?
          </Button>
        </div>

        {/* Rules & Privacy */}
        <div className="max-w-[20rem]">
          <TermsAndPrivacy track={track} />
        </div>
      </div>
    </div>
  );
};
